import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, BarChart3, ChevronDown, Clock, CreditCard, Flame, Gauge, LifeBuoy, LineChart, LogOut, Moon, Sun, UserRound, WalletCards } from "lucide-react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./uikit";
import { API_CONFIG } from "../config/endpoints";
import { useAuth } from "../context/AuthContext";
import { useScan } from "../context/ScanContext";
import { ThemePreference, useTheme } from "../context/ThemeContext";
import { fetchScanStreak, ScanStreak } from "../lib/apiClient";

// Batman-*inspired* dark theme, deliberately not Batman-*branded*: no bat
// emblem, no "Batman" wordmark anywhere a user or an app-store listing
// would see it - shipping DC's actual character name/mark inside a paid
// product is a real trademark risk, not just a naming-confusion one (see
// the earlier gROOT naming conversation for that distinction). "Gotham
// mode" - now the theme's official user-facing name everywhere it's
// offered - keeps the reference purely aesthetic: a near-black palette
// with the brand's own gold carried through as the accent, nothing DC
// owns.
//
// One three-way switch living directly in the top bar (next to the scan
// streak), not a single toggle plus a separate dropdown menu section -
// the two used to disagree about how many states there were (the icon
// button only ever knew light/dark, the dropdown had the real Auto/Light/
// Gotham three-way choice). One control, one place, is the only way that
// can't drift out of sync with itself again.
const THEME_OPTIONS: { value: ThemePreference; label: string; icon: typeof Clock }[] = [
  { value: "auto", label: "Auto (night mode 10pm-6am)", icon: Clock },
  { value: "light", label: "Light mode", icon: Sun },
  { value: "dark", label: "Gotham mode", icon: Moon },
];

const ThemeSwitch: React.FC = () => {
  const { themePreference, setThemePreference } = useTheme();
  return (
    <div
      // Always visible, not just from sm: up - the dropdown menu no longer
      // has an appearance fallback, so hiding this on mobile would leave
      // phone users with no way to change the theme at all. Buttons are a
      // touch smaller at the base size (still comfortably tap-sized) and
      // grow slightly from sm: up, rather than the whole control
      // disappearing below that breakpoint.
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-[hsl(var(--line))] bg-card p-0.5"
      role="group"
      aria-label="Appearance"
      data-testid="header-theme-switch"
    >
      {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = themePreference === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setThemePreference(value)}
            aria-label={label}
            aria-pressed={active}
            title={label}
            data-testid={`header-theme-switch-${value}`}
            className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors sm:h-7 sm:w-7 ${
              active
                ? "bg-[hsl(var(--honey-wash))] text-[hsl(var(--honey-deep))]"
                : "text-[hsl(var(--muted-ink))] hover:text-[hsl(var(--ink))]"
            }`}
          >
            <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          </button>
        );
      })}
    </div>
  );
};

// Purely a delight/retention touch, never a blocker - if the streak fetch
// fails or the endpoint isn't deployed yet, this renders nothing and stays
// silent (no toast, no retry), the same way a missing "nice to have" widget
// should fail. current_streak resets to 0 server-side after a missed day,
// so this only ever shows a genuinely live streak.
const StreakBadge: React.FC = () => {
  const [streak, setStreak] = useState<ScanStreak | null>(null);
  // Header is mounted once at the app root, above the router (see
  // AppRoot.tsx) - it never remounts as the user navigates. The original
  // mount-only fetch (empty dependency array) meant the badge froze at
  // whatever streak was live when the app first loaded and never updated
  // again for the rest of the session, even after completing new scans -
  // reported live as "the scan streak doesn't update". Re-running the
  // fetch on every route change (ScanPage's "Analyse Stock" always
  // navigates to /dashboard on success) picks up the just-incremented
  // count without needing a full page reload.
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    fetchScanStreak()
      .then((res) => { if (!cancelled) setStreak(res); })
      .catch(() => { if (!cancelled) setStreak(null); });
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (!streak || streak.current_streak < 1) return null;
  // The backend has always returned longest_streak alongside
  // current_streak, but nothing surfaced it - only mention "best" when it's
  // actually informative (today isn't already the record), same reasoning
  // as showing "(unsynced)" only when a value is genuinely stale rather
  // than always appending a redundant qualifier.
  const isPersonalBest = streak.longest_streak <= streak.current_streak;
  const tooltip = isPersonalBest
    ? `${streak.current_streak}-day scan streak - your best yet`
    : `${streak.current_streak}-day scan streak (best: ${streak.longest_streak})`;
  return (
    <span
      // Full pill chrome (border/background/padding) only from sm: up, where
      // there's room - on narrow phones this is just the flame + number so
      // it never crowds out the plan pill or account menu.
      className="inline-flex items-center gap-0.5 rounded-full px-1 py-1 text-[11px] font-extrabold text-[hsl(var(--ink))] sm:gap-1 sm:border sm:border-[hsl(var(--line))] sm:bg-card sm:px-2.5 sm:py-1.5"
      data-testid="header-streak-badge"
      title={tooltip}
    >
      <Flame className="h-3.5 w-3.5 text-[hsl(24_88%_54%)]" /> {streak.current_streak}
    </span>
  );
};

// This app has no server-side subscription-cancellation API integration -
// SureCart's own customer billing portal is the only way a subscriber can
// cancel, update payment method, or view invoices. Set
// REACT_APP_SURECART_PORTAL_URL to enable this link.
function openManageSubscription() {
  if (!API_CONFIG.manageSubscriptionUrl) {
    toast.error("Subscription management isn't configured yet. Contact support to cancel or change your plan.");
    return;
  }
  window.open(API_CONFIG.manageSubscriptionUrl, "_blank", "noopener,noreferrer");
}

function planSummary(usage: ReturnType<typeof useAuth>["usage"], initialising: boolean): string {
  // plan === "unavailable" covers two different situations that read very
  // differently to a user: a session that simply hasn't finished its first
  // sync yet (every fresh login passes through this for a moment) versus a
  // sync that was actually attempted and failed. The first is completely
  // normal and "Plan unavailable" reads like an account problem right when
  // someone opens the app - reserve that wording for a confirmed failure,
  // and say something neutral while still loading.
  if (initialising && (!usage || usage.plan === "unavailable")) return "Checking plan...";
  // sync_error alone just means the *last* refresh failed — show the last
  // known-good plan/credits rather than claiming the plan is gone.
  if (!usage || usage.plan === "unavailable") return "Plan unavailable";
  const plan = String(usage.plan || "free");
  const stale = usage.sync_error ? " (unsynced)" : "";
  if (usage.admin || plan === "admin") return `Admin - Unlimited access${stale}`;
  if (plan === "pro") {
    const monthly = Number(usage.pro_monthly_scans_remaining || 0);
    const extra = Number(usage.extra_scans_remaining || 0);
    return `Pro - ${monthly} monthly${extra ? ` + ${extra} extra` : ""}${stale}`;
  }
  if (plan === "standard") return `Standard - ${Number(usage.standard_scans_remaining || 0)} scans${stale}`;
  if (plan === "starter") return `Starter - ${Number(usage.starter_scans_remaining || 0)} scans${stale}`;
  return `Free - ${Number(usage.free_scans_remaining || 0)}/5 scans${stale}`;
}

export const Header: React.FC = () => {
  const { session, usage, isPro, logout, initialising } = useAuth();
  const { clear: clearScan } = useScan();
  const navigate = useNavigate();
  const location = useLocation();
  const plan = String(usage?.plan || "unavailable");
  const onPricingPage = location.pathname === "/pricing";

  // Signing out must wipe the previous account's scan/checklist/peer state too -
  // ScanProvider is mounted above the router and outlives login sessions, so
  // without this the next person to log in on this browser (same account or a
  // different one on a shared device) would see the prior user's last scan
  // rendered immediately on /dashboard, before running any scan of their own.
  const signOut = () => {
    clearScan();
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[hsl(var(--line))] bg-card/90 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8">
        <button className="flex items-baseline gap-2 outline-none" onClick={() => navigate("/scan")} aria-label="Tenet home">
          <span className="font-wordmark text-[19px] text-[hsl(var(--ink))] sm:text-[22px]">Financify</span>
          <span className="hidden rounded-md border border-[hsl(var(--line))] bg-[hsl(var(--cream-2))] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-ink))] sm:inline-flex">Tenet</span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeSwitch />

          {session ? <StreakBadge /> : null}

          {/* For a paying/free-tier user, this pill is the only place their
              live remaining-scan count shows without opening the menu -
              genuinely useful to glance at. For an admin, "Unlimited
              access" never changes and is already shown in the dropdown's
              own plan line (see the DropdownMenuLabel below) - repeating
              it here a second time, permanently, in the top bar has no
              information value, just repetition. Reported live as
              pointless duplication. */}
          {session && !(usage?.admin || usage?.plan === "admin") ? (
            // Icon-only on mobile, full text from sm: up - the top bar now
            // also carries the theme switch (previously sm:-and-up only,
            // now always visible so phones have a way to change theme at
            // all), and this pill's full text was the single biggest thing
            // in the bar, wide enough on its own to push the account menu
            // button off a narrow screen. Same "shed your chrome/text on
            // mobile" pattern the streak badge already uses just above -
            // nothing is lost, a tap still opens the Account page with the
            // full detail.
            <button onClick={() => navigate("/account")} className="flex max-w-[46vw] items-center gap-1.5 truncate rounded-full border border-[hsl(var(--honey)/0.35)] bg-[hsl(var(--honey-wash))] px-2 py-1.5 text-[11px] font-extrabold text-[hsl(30_60%_25%)] dark:text-[hsl(43_90%_78%)] sm:max-w-none sm:px-3 sm:text-xs" data-testid="header-usage-counter" title={planSummary(usage, initialising)}>
              <Gauge className="h-3.5 w-3.5 shrink-0" /> <span className="hidden truncate sm:inline">{planSummary(usage, initialising)}</span>
            </button>
          ) : null}

          {session && !isPro && !onPricingPage ? (
            <Button size="sm" onClick={() => navigate("/pricing")} className="hidden bg-[hsl(var(--honey))] font-extrabold text-[hsl(var(--ink))] hover:bg-[hsl(var(--honey-2))] md:inline-flex">
              View plans <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          ) : null}

          {session ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 border-[hsl(var(--line))] bg-card font-bold" data-testid="header-account-menu-trigger">
                  <UserRound className="h-4 w-4" /><span className="hidden max-w-[140px] truncate sm:inline">{session.email}</span><ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>
                  <div className="text-xs font-semibold text-[hsl(var(--muted-ink))]">Signed in as</div>
                  <div className="truncate text-sm font-extrabold">{session.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* The header pill next to this menu already shows the live
                    plan/usage summary at all times - repeating both a
                    separate "Current plan" badge and the same summary text
                    again here just restated the pill twice more before you'd
                    even opened the menu. One line, badge and detail
                    together. */}
                <DropdownMenuLabel className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-semibold text-[hsl(var(--muted-ink))]">{planSummary(usage, initialising)}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${isPro ? "bg-[hsl(var(--ink-solid))] text-[hsl(var(--honey-2))]" : "bg-[hsl(var(--cream-2))]"}`}>{plan}</span>
                </DropdownMenuLabel>
                {usage?.cancel_at_period_end ? <DropdownMenuLabel className="text-xs text-[hsl(var(--warning))]">{usage.subscription_plan ? `${usage.subscription_plan[0].toUpperCase()}${usage.subscription_plan.slice(1)}` : "Pro"} access ends after the current billing cycle.</DropdownMenuLabel> : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer font-bold"><WalletCards className="mr-1.5 h-4 w-4" /> Account and balances</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/track-record")} className="cursor-pointer font-bold" data-testid="header-track-record-link"><LineChart className="mr-1.5 h-4 w-4" /> Your track record</DropdownMenuItem>
                {usage?.subscription_plan ? <DropdownMenuItem onClick={openManageSubscription} className="cursor-pointer font-bold"><CreditCard className="mr-1.5 h-4 w-4" /> Manage subscription</DropdownMenuItem> : null}
                {usage?.admin || usage?.plan === "admin" ? <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer font-bold"><BarChart3 className="mr-1.5 h-4 w-4" /> Payment operations</DropdownMenuItem> : null}
                <DropdownMenuItem onClick={() => navigate("/pricing")} className="cursor-pointer font-bold"><ArrowUpRight className="mr-1.5 h-4 w-4" /> Pricing and plans</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/support")} className="cursor-pointer font-bold" data-testid="header-support-link"><LifeBuoy className="mr-1.5 h-4 w-4" /> Help and support</DropdownMenuItem>
                <DropdownMenuItem onClick={signOut} className="cursor-pointer font-bold text-[hsl(var(--destructive))]"><LogOut className="mr-1.5 h-4 w-4" /> Sign out of this device</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
    </header>
  );
};
